/**
 * Cliente HTTP del CMS.
 *
 * El reintento existe porque el API se cae a ratos, pero reintentar sin
 * límites es peor que no reintentar: la versión anterior encadenaba cuatro
 * intentos sin timeout ni espera, así que un CMS caído dejaba la página en
 * blanco hasta que el navegador se rindiera. Aquí cada intento tiene su corte,
 * el conjunto tiene un presupuesto total, y sólo se reintenta lo que puede
 * arreglarse solo.
 */

/** Corte de un intento suelto. Por encima de esto el CMS no va a responder. */
const TIMEOUT_MS = 8000;
/** Techo de la operación completa, reintentos y esperas incluidos. */
const PRESUPUESTO_MS = 12000;
/** Primera espera entre intentos; se duplica en cada vuelta. */
const ESPERA_BASE_MS = 300;

export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly statusText: string,
  ) {
    super(`Error ${status}: ${statusText}`);
    this.name = "HttpError";
  }
}

/**
 * Un 4xx es culpa de la petición y va a fallar igual las tres veces: se
 * reintenta sólo lo que depende del servidor o de la red.
 */
function vaARepetirse(err: unknown): boolean {
  if (err instanceof HttpError) return err.status >= 500;
  return true; // red caída, timeout o JSON roto: puede salir bien al segundo
}

const dormir = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export async function fetchWithRetry<T>(
  endpoint: string,
  options: RequestInit = {},
  retries = 3,
  pasajeros?: boolean,
): Promise<T> {
  const API_BASE_URL = pasajeros
    ? process.env.NEXT_PUBLIC_ATENCION_PASAJEROS_API_URL!
    : process.env.NEXT_PUBLIC_DATOS_API_URL!;
  const API_KEY = pasajeros
    ? (process.env.NEXT_PUBLIC_ATENCION_PASAJEROS_API_KEY ?? "")
    : (process.env.NEXT_PUBLIC_DATOS_API_KEY ?? "");

  const url = `${API_BASE_URL}${endpoint}`;
  const limite = Date.now() + PRESUPUESTO_MS;
  let ultimo: unknown = null;

  for (let intento = 0; intento <= retries; intento++) {
    const restante = limite - Date.now();
    // Sin margen para otro intento completo, se devuelve el último fallo en
    // vez de lanzar una petición que se va a cortar a mitad.
    if (restante <= 0) break;

    const control = new AbortController();
    const corte = setTimeout(
      () => control.abort(),
      Math.min(TIMEOUT_MS, restante),
    );

    try {
      const res = await fetch(url, {
        ...options,
        headers: {
          ...(options.headers || {}),
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
        },
        cache: "no-store",
        signal: control.signal,
      });

      if (!res.ok) throw new HttpError(res.status, res.statusText);

      return (await res.json()) as T;
    } catch (err) {
      ultimo = err;
      if (!vaARepetirse(err) || intento === retries) break;

      // Backoff exponencial: si el CMS está saturado, insistir de inmediato
      // sólo añade carga. Nunca se espera más de lo que queda de presupuesto.
      const espera = ESPERA_BASE_MS * 2 ** intento;
      const margen = limite - Date.now();
      if (margen <= espera) break;
      await dormir(espera);
    } finally {
      clearTimeout(corte);
    }
  }

  throw ultimo instanceof Error
    ? ultimo
    : new Error(`No se pudo cargar ${endpoint}`);
}
